<template>
  <f7-actions
    v-model:opened="open"
    class="main-uc-confirmation content-action-sheet"
  >
    <f7-block>
      <div class="content-action-sheet__title text-align-center">
        <span data-testid="uc-confirmation-title">
          {{ title }}
        </span>
      </div>

      <f7-actions-label class="content-action-sheet__subtitle text-align-center">
        {{ textConfirmation }}
      </f7-actions-label>

      <div class="content-action-sheet__buttons">
        <f7-button
          class="content-action-sheet__buttons--cancel"
          fill
          @click="handleCancel"
        >
          {{ cancelText }}
        </f7-button>

        <f7-button
          v-if="!options.hideOkButton"
          class="content-action-sheet__buttons--confirm"
          fill
          @click="handleOk"
        >
          {{ confirmText }}
        </f7-button>
      </div>
    </f7-block>
  </f7-actions>
</template>

<script setup>
// @ts-check
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

/** @import { Ref } from 'vue' */

const props = defineProps({
  cancel: {
    type: String,
    default: null,
  },
  confirm: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  'cancel',
  'ok',
]);

const { t } = useI18n();
const open = ref(false);
const title = ref('');
const textConfirmation = ref('');
/** @type {Ref<Function | undefined>} */
const okCallback = ref(undefined);
/** @type {Ref<Function | undefined>} */
const cancelCallback = ref(undefined);
const options = reactive({
  hideOkButton: false,
});

const cancelText = computed(() => props.cancel ?? t('common.cancel'));
const confirmText = computed(() => props.confirm ?? t('common.confirm'));

/**
 * @public
 * @param {string} actionsTitle
 * @param {string} actionsText
 * @param {Function} actionsOkCallback
 * @param {Function} [actionsCancelCallback]
 * @param {object} [options]
 * @param {boolean} [options.hideOkButton=false]
 * @returns {void}
 */
function create(
  actionsTitle,
  actionsText,
  actionsOkCallback,
  actionsCancelCallback,
  {
    hideOkButton = false,
  } = {},
) {
  title.value = actionsTitle;
  textConfirmation.value = actionsText;
  okCallback.value = actionsOkCallback;
  cancelCallback.value = actionsCancelCallback;
  options.hideOkButton = !!hideOkButton;
}

/**
 * @public
 * @returns {void}
 */
function show() {
  open.value = true;
}

async function handleCancel() {
  if (!open.value) {
    return;
  }

  open.value = false;
  emit('cancel');
  cancelCallback.value?.();
}

async function handleOk() {
  open.value = false;
  emit('ok');
  okCallback.value?.();
}

defineExpose({
  create,
  show,
});
</script>

<style lang="scss">
.content-action-sheet {
  background-color: #fff;

  &__title {
    color: var(--his-darkness);
    font-size: 16px;
    font-weight: 500;
    margin-top: 15px;
    margin-bottom: 10px;
  }

  &__buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &--cancel {
      color: var(--his-darkgray);
      background-color: var(--his-bg);
    }

    &--confirm {
      background-color: var(--his-dark-blue);
    }
  }

  .actions-label::after {
    height: 0 !important;
  }
}
</style>
